import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_BASE_URL, API_KEY, getApiHeaders } from '../config/api';
import { LIVE_QUEST_REWARD_SLOTS, liveQuestProductKey } from '../data/liveQuestRewards';
import { readImageSize, readVideoSize } from '../utils/profileFrameRules';

const walletApiBase = `${API_BASE_URL.replace(/\/$/, '')}/wallet/t`;

const MAX_FILES = 100;
const LIVE_QUEST_FRAME_REWARDS = LIVE_QUEST_REWARD_SLOTS.filter((reward) => reward.rewardType === 'profileFrame');

type UploadStatus = 'pending' | 'uploading' | 'done' | 'error';
type FrameTypeOption = 'profile_frame' | 'profile_frame_animated';

type WalletShopMediaAsset = {
  Type: string;
  Url: string;
  MimeType?: string;
  Width?: number;
  Height?: number;
};

type ExistingFrame = {
  ProductKey: string;
  Name?: string;
  Environment?: 'dev' | 'prod';
  Type?: string;
  MediaAssets?: WalletShopMediaAsset[];
  DisplayOrder?: number;
};

type FrameFileItem = {
  id: string;
  file: File;
  productKey: string;
  name: string;
  previewUrl: string;
  width?: number;
  height?: number;
  dimensionWarning?: string;
  status: UploadStatus;
  error?: string;
  assetUrl?: string;
  savedProductKey?: string;
};

const fileNameToKey = (filename: string): string =>
  filename
    .replace(/\.[^/.]+$/, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .toUpperCase();

const fileNameToLabel = (filename: string): string => {
  const base = filename.replace(/\.[^/.]+$/, '');
  return base
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
};

const BulkFrameUpload: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const requestedReward = searchParams.get('liveQuest') || '';
  const initialType: FrameTypeOption = searchParams.get('type') === 'profile_frame_animated' ? 'profile_frame_animated' : 'profile_frame';
  const initialEnv: 'dev' | 'prod' = searchParams.get('env') === 'dev' ? 'dev' : 'prod';

  const [frameType, setFrameType] = useState<FrameTypeOption>(initialType);
  const [liveQuestOnly, setLiveQuestOnly] = useState(!!requestedReward);
  const [liveQuestRewardId, setLiveQuestRewardId] = useState(requestedReward);

  const [sharedSettings, setSharedSettings] = useState({
    CurrencyType: 'coin' as 'coin' | 'token',
    Price: '1000',
    DisplayOrderStart: '1',
    AvailabilityType: 'unlimited' as 'unlimited' | 'limited',
    QuantityAvailable: '100',
    Environment: initialEnv,
    ThemePreference: 'adaptive' as 'adaptive' | 'light' | 'dark',
    IsActive: true,
  });

  const [frameFiles, setFrameFiles] = useState<FrameFileItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [existingFrames, setExistingFrames] = useState<Map<string, ExistingFrame>>(new Map());

  // Fetch existing profile frames once to calculate next DisplayOrder and check for key collision
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token || !API_KEY) return;

    fetch(`${walletApiBase}/getWalletShopProductsV2`, {
      method: 'POST',
      headers: getApiHeaders(token),
      body: JSON.stringify({
        UserID: 'dashboard',
        CategoryKey: 'PROFILE',
        Type: frameType,
        IncludeInactive: true,
      }),
    })
      .then((r) => r.json())
      .then((json) => {
        const products: ExistingFrame[] = json?.data?.products || [];
        const maxOrder = products.reduce((max, p) => Math.max(max, p.DisplayOrder ?? 0), 0);
        setSharedSettings((prev) => ({ ...prev, DisplayOrderStart: String(maxOrder + 1) }));
        setExistingFrames(new Map(products.map((p) => [p.ProductKey, p])));
      })
      .catch(() => {});
  }, [frameType]);

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      frameFiles.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    };
  }, [frameFiles]);

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key. Set REACT_APP_API_KEY in your .env file.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;

    const remaining = MAX_FILES - frameFiles.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_FILES} frames allowed.`);
      return;
    }

    const capped = selected.slice(0, remaining);
    if (capped.length < selected.length) {
      toast.warn(`Only ${capped.length} of ${selected.length} files added (${MAX_FILES} max).`);
    }

    const newEntries: FrameFileItem[] = capped.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
      file,
      productKey: fileNameToKey(file.name),
      name: fileNameToLabel(file.name),
      previewUrl: URL.createObjectURL(file),
      status: 'pending',
    }));

    setFrameFiles((prev) => [...prev, ...newEntries]);
    e.target.value = '';

    // Inspect dimensions asynchronously in background
    newEntries.forEach(async (entry) => {
      try {
        if (entry.file.type.startsWith('image/')) {
          const dims = await readImageSize(entry.file);
          setFrameFiles((prev) =>
            prev.map((f) =>
              f.id === entry.id
                ? {
                    ...f,
                    width: dims.Width,
                    height: dims.Height,
                    dimensionWarning:
                      dims.Width !== 1024 || dims.Height !== 1024
                        ? `${dims.Width}×${dims.Height} (recommended: 1024×1024)`
                        : undefined,
                  }
                : f
            )
          );
        } else if (entry.file.type.startsWith('video/')) {
          const dims = await readVideoSize(entry.file);
          if (dims) {
            setFrameFiles((prev) =>
              prev.map((f) =>
                f.id === entry.id
                  ? {
                      ...f,
                      width: dims.Width,
                      height: dims.Height,
                    }
                  : f
              )
            );
          }
        }
      } catch {
        // Continue if dimension measurement fails
      }
    });
  };

  const removeFrame = (id: string) => {
    setFrameFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  };

  const updateField = (id: string, field: 'productKey' | 'name', value: string) => {
    setFrameFiles((prev) =>
      prev.map((f) =>
        f.id === id
          ? {
              ...f,
              [field]: field === 'productKey' ? value.replace(/\s+/g, '_').toUpperCase() : value,
            }
          : f
      )
    );
  };

  const handleUploadAll = async () => {
    const headers = headerWithAuth();
    if (!headers) return;

    if (!frameFiles.length) {
      toast.error('Add at least one frame file.');
      return;
    }

    if (liveQuestOnly && !liveQuestRewardId) {
      toast.error('Choose which Live Quest reward these frames belong to.');
      return;
    }

    const priceNum = Number(sharedSettings.Price);
    if (!liveQuestOnly && (isNaN(priceNum) || priceNum <= 0)) {
      toast.error(`Price in ${sharedSettings.CurrencyType}s must be a number greater than 0.`);
      return;
    }

    const displayOrderStart = Number(sharedSettings.DisplayOrderStart);
    if (isNaN(displayOrderStart) || displayOrderStart < 0) {
      toast.error('Display Order Start must be a valid non-negative number.');
      return;
    }

    const pending = frameFiles.filter((f) => f.status === 'pending' || f.status === 'error');
    if (!pending.length) {
      toast.info('All frames are already uploaded.');
      return;
    }

    setUploading(true);
    setCompletedCount(0);
    let successCount = 0;

    for (let i = 0; i < pending.length; i++) {
      const item = pending[i];
      const productKey = liveQuestOnly
        ? liveQuestRewardId
          ? liveQuestProductKey(liveQuestRewardId, pending.length > 1 ? String(i + 1) : undefined)
          : item.productKey
        : item.productKey;

      setFrameFiles((prev) =>
        prev.map((f) => (f.id === item.id ? { ...f, status: 'uploading', error: undefined } : f))
      );

      try {
        // Step 1: get presigned upload URL
        const assetType = frameType === 'profile_frame_animated' ? 'thumbnail' : 'image';
        const presignedRes = await fetch(`${walletApiBase}/createWalletAssetUploadPresignedUrl`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            ProductKey: productKey,
            ContentType: item.file.type || 'image/png',
            AssetType: assetType,
            AssetKind: frameType,
            FileSize: item.file.size,
            ...(item.width ? { Width: item.width } : {}),
            ...(item.height ? { Height: item.height } : {}),
          }),
        });
        const presignedJson = await presignedRes.json();
        if (!presignedRes.ok) throw new Error(presignedJson.message || 'Failed to get upload URL');

        const uploadData = presignedJson.data;

        // Step 2: upload asset directly to R2
        const uploadRes = await fetch(uploadData.UploadUrl, {
          method: 'PUT',
          body: await item.file.arrayBuffer(),
        });
        if (!uploadRes.ok) throw new Error('Failed to upload image to storage');

        const assetUrl: string = uploadData.AssetUrl;

        // Step 3: Create or update product
        const existing = existingFrames.get(productKey);
        const newMediaAsset: WalletShopMediaAsset = {
          Type: assetType,
          Url: assetUrl,
          MimeType: item.file.type || 'image/png',
          ...(item.width ? { Width: item.width } : {}),
          ...(item.height ? { Height: item.height } : {}),
        };

        if (existing) {
          const existingAssets = existing.MediaAssets || [];
          const nextAssets: WalletShopMediaAsset[] = existingAssets.some((a) => a.Type === assetType)
            ? existingAssets.map((a) => (a.Type === assetType ? newMediaAsset : a))
            : [...existingAssets, newMediaAsset];

          const updateRes = await fetch(`${walletApiBase}/updateWalletShopProduct`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              ProductKey: productKey,
              Environment: existing.Environment || sharedSettings.Environment,
              MediaAssets: nextAssets,
            }),
          });
          const updateJson = await updateRes.json();
          if (!updateRes.ok) throw new Error(updateJson.message || 'Failed to update existing frame');
        } else {
          const isCoin = sharedSettings.CurrencyType === 'coin';
          const productRes = await fetch(`${walletApiBase}/createWalletShopProduct`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              ProductKey: productKey,
              Name: item.name,
              Description: `${item.name} profile frame.`,
              CategoryKey: 'PROFILE',
              Type: frameType,
              ThemePreference: sharedSettings.ThemePreference,
              CurrencyType: sharedSettings.CurrencyType,
              PriceCoins: isCoin ? (liveQuestOnly ? 0 : priceNum) : 0,
              ...(sharedSettings.CurrencyType === 'token' && !liveQuestOnly ? { PriceTokens: priceNum } : {}),
              AvailabilityType: sharedSettings.AvailabilityType,
              ...(sharedSettings.AvailabilityType === 'limited'
                ? { QuantityAvailable: Number(sharedSettings.QuantityAvailable) }
                : {}),
              DisplayOrder: displayOrderStart + i,
              Environment: sharedSettings.Environment,
              IsActive: !liveQuestOnly && sharedSettings.IsActive,
              ...(liveQuestOnly
                ? {
                    Tags: ['live_quest', 'profile_frame', liveQuestRewardId],
                    Metadata: { LiveQuestOnly: true, LiveQuestRewardId: liveQuestRewardId },
                  }
                : {}),
              MediaAssets: [newMediaAsset],
            }),
          });
          const productJson = await productRes.json();
          if (!productRes.ok) throw new Error(productJson.message || 'Failed to create profile frame');
        }

        successCount++;
        setCompletedCount(successCount);
        setFrameFiles((prev) =>
          prev.map((f) =>
            f.id === item.id
              ? {
                  ...f,
                  status: 'done',
                  assetUrl,
                  savedProductKey: productKey,
                  error: undefined,
                }
              : f
          )
        );
      } catch (err: any) {
        setFrameFiles((prev) =>
          prev.map((f) =>
            f.id === item.id
              ? {
                  ...f,
                  status: 'error',
                  error: err?.message || 'Failed to upload',
                }
              : f
          )
        );
      }
    }

    setUploading(false);
    if (successCount === pending.length) {
      toast.success(`Successfully uploaded all ${successCount} frame${successCount === 1 ? '' : 's'}!`);
    } else {
      toast.warn(`Uploaded ${successCount} of ${pending.length} frames.`);
    }
  };

  const pendingCount = frameFiles.filter((f) => f.status === 'pending' || f.status === 'error').length;
  const doneCount = frameFiles.filter((f) => f.status === 'done').length;

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6'>
        {/* Header */}
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Bulk Frame Upload</h1>
            <p className='text-sm text-gray-400'>
              Set shared settings once, select up to {MAX_FILES} profile frame images, then upload all.
            </p>
          </div>
          <button
            onClick={() => navigate(liveQuestOnly ? '/live-quest-assets' : `/products?category=PROFILE&env=${sharedSettings.Environment}`)}
            className='px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
          >
            {liveQuestOnly ? 'Back to Live Quest Assets' : 'Back to Wallet Assets'}
          </button>
        </div>

        {/* Frame Type Selector */}
        <div className='bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-4'>
          <div>
            <div className='text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1'>Frame Type</div>
            <div className='flex items-center gap-2'>
              <button
                type='button'
                onClick={() => setFrameType('profile_frame')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
                  frameType === 'profile_frame'
                    ? 'border-[#FF9C15] text-white bg-white/10'
                    : 'border-white/10 text-gray-300 hover:text-white hover:border-white/30'
                }`}
              >
                Profile Frame (Static)
              </button>
              <button
                type='button'
                onClick={() => setFrameType('profile_frame_animated')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
                  frameType === 'profile_frame_animated'
                    ? 'border-[#FF9C15] text-white bg-white/10'
                    : 'border-white/10 text-gray-300 hover:text-white hover:border-white/30'
                }`}
              >
                Animated Profile Frame
              </button>
            </div>
          </div>
          <p className='text-xs text-gray-400 max-w-md'>
            {frameType === 'profile_frame'
              ? 'Static frames require one PNG image (1024×1024 px, max 1 MB).'
              : 'Animated frames use a WebP preview thumbnail (and can attach .webm and .mov videos later).'}
          </p>
        </div>

        {/* Live Quest Reward Linking */}
        <div className='bg-fuchsia-500/10 border border-fuchsia-400/20 rounded-2xl p-6'>
          <label className='flex items-start gap-3 text-sm text-white cursor-pointer'>
            <input
              type='checkbox'
              checked={liveQuestOnly}
              onChange={(e) => setLiveQuestOnly(e.target.checked)}
              className='mt-1'
            />
            <span>
              <span className='font-semibold'>Live Quest reward only</span>
              <span className='block text-xs text-gray-300'>
                Frame is awarded to users for completing a Live Quest level. Free (Price: 0) and kept inactive so it never appears for sale in the Shop.
              </span>
            </span>
          </label>
          {liveQuestOnly && (
            <div className='mt-4 max-w-md'>
              <label className='block text-xs text-gray-300 mb-1'>Reward this frame belongs to *</label>
              <select
                value={liveQuestRewardId}
                onChange={(e) => setLiveQuestRewardId(e.target.value)}
                className='w-full px-3 py-2 bg-slate-900 border border-white/20 rounded-lg text-white'
              >
                <option value=''>Choose a Live Quest reward slot</option>
                {LIVE_QUEST_FRAME_REWARDS.map((reward) => (
                  <option key={reward.id} value={reward.id}>
                    {reward.track === 'plus' ? 'Plus' : 'Free'} LV {reward.level}: {reward.title}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Shared Settings */}
        <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6'>
          <h2 className='text-lg font-semibold text-white mb-2'>Shared Settings</h2>
          <p className='text-xs text-gray-400 mb-4'>
            These values apply to every frame in this batch. Product Key and Name are derived from file names and can be customized below.
          </p>
          <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4'>
            {!liveQuestOnly && (
              <div>
                <label className='block text-xs text-gray-400 mb-1'>Currency *</label>
                <select
                  value={sharedSettings.CurrencyType}
                  onChange={(e) =>
                    setSharedSettings((prev) => ({
                      ...prev,
                      CurrencyType: e.target.value as 'coin' | 'token',
                    }))
                  }
                  className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-xs'
                >
                  <option value='coin'>Coins</option>
                  <option value='token'>Tokens</option>
                </select>
              </div>
            )}
            {!liveQuestOnly && (
              <div>
                <label className='block text-xs text-gray-400 mb-1'>
                  Price ({sharedSettings.CurrencyType === 'coin' ? 'Coins' : 'Tokens'}) *
                </label>
                <input
                  type='number'
                  min='1'
                  value={sharedSettings.Price}
                  onChange={(e) => setSharedSettings((prev) => ({ ...prev, Price: e.target.value }))}
                  className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-xs'
                />
              </div>
            )}
            <div>
              <label htmlFor='display-order-start' className='block text-xs text-gray-400 mb-1'>Display Order Start</label>
              <input
                id='display-order-start'
                type='number'
                min='0'
                value={sharedSettings.DisplayOrderStart}
                onChange={(e) => setSharedSettings((prev) => ({ ...prev, DisplayOrderStart: e.target.value }))}
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-xs'
              />
              <p className='text-[10px] text-gray-500 mt-1'>Auto-increments +1</p>
            </div>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>Availability</label>
              <select
                value={sharedSettings.AvailabilityType}
                onChange={(e) =>
                  setSharedSettings((prev) => ({
                    ...prev,
                    AvailabilityType: e.target.value as 'unlimited' | 'limited',
                  }))
                }
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-xs'
              >
                <option value='unlimited'>Unlimited</option>
                <option value='limited'>Limited</option>
              </select>
            </div>
            {sharedSettings.AvailabilityType === 'limited' && (
              <div>
                <label className='block text-xs text-gray-400 mb-1'>Quantity *</label>
                <input
                  type='number'
                  min='1'
                  value={sharedSettings.QuantityAvailable}
                  onChange={(e) => setSharedSettings((prev) => ({ ...prev, QuantityAvailable: e.target.value }))}
                  className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-xs'
                />
              </div>
            )}
            <div>
              <label className='block text-xs text-gray-400 mb-1'>Environment</label>
              <select
                value={sharedSettings.Environment}
                onChange={(e) =>
                  setSharedSettings((prev) => ({
                    ...prev,
                    Environment: e.target.value as 'dev' | 'prod',
                  }))
                }
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-xs'
              >
                <option value='prod'>prod</option>
                <option value='dev'>dev</option>
              </select>
            </div>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>Theme Preference</label>
              <select
                value={sharedSettings.ThemePreference}
                onChange={(e) =>
                  setSharedSettings((prev) => ({
                    ...prev,
                    ThemePreference: e.target.value as 'adaptive' | 'light' | 'dark',
                  }))
                }
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-xs'
              >
                <option value='adaptive'>Adaptive</option>
                <option value='light'>Light</option>
                <option value='dark'>Dark</option>
              </select>
            </div>
            {!liveQuestOnly && (
              <div className='flex flex-col justify-center pt-3'>
                <label className='flex items-center gap-2 cursor-pointer text-xs text-white'>
                  <input
                    type='checkbox'
                    checked={sharedSettings.IsActive}
                    onChange={(e) => setSharedSettings((prev) => ({ ...prev, IsActive: e.target.checked }))}
                    className='rounded'
                  />
                  <span>Active in Shop</span>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* File Picker */}
        <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6'>
          <div className='flex items-center justify-between mb-4'>
            <div>
              <h2 className='text-lg font-semibold text-white'>Frame Image Files</h2>
              <p className='text-xs text-gray-400 mt-0.5'>
                {frameFiles.length} / {MAX_FILES} files added
                {doneCount > 0 && ` · ${doneCount} uploaded`}
              </p>
            </div>
            <label className='cursor-pointer px-4 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'>
              + Add Frame Files
              <input
                type='file'
                accept={frameType === 'profile_frame' ? 'image/png,image/webp,image/jpeg' : 'image/webp,image/png'}
                multiple
                className='hidden'
                onChange={handleFilesSelected}
                disabled={uploading || frameFiles.length >= MAX_FILES}
              />
            </label>
          </div>

          {frameFiles.length === 0 ? (
            <div className='border-2 border-dashed border-white/10 rounded-xl py-16 flex flex-col items-center justify-center gap-3'>
              <div className='w-14 h-14 rounded-full bg-white/5 flex items-center justify-center text-gray-400'>
                <svg className='w-7 h-7' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M4 4h16v16H4V4zm3 3h10v10H7V7z' />
                </svg>
              </div>
              <p className='text-sm text-gray-400'>No frame files selected yet.</p>
              <label className='cursor-pointer px-5 py-2.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition'>
                Select Frame Images
                <input
                  type='file'
                  accept={frameType === 'profile_frame' ? 'image/png,image/webp,image/jpeg' : 'image/webp,image/png'}
                  multiple
                  className='hidden'
                  onChange={handleFilesSelected}
                  disabled={uploading}
                />
              </label>
              <p className='text-xs text-gray-500'>
                {frameType === 'profile_frame'
                  ? 'Up to 100 files · PNG recommended (1024×1024 px, max 1 MB)'
                  : 'Up to 100 files · WebP recommended'}
              </p>
            </div>
          ) : (
            <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4'>
              {frameFiles.map((frame) => (
                <div
                  key={frame.id}
                  className={`relative bg-white/5 border rounded-xl overflow-hidden flex flex-col ${
                    frame.status === 'done'
                      ? 'border-emerald-400/40'
                      : frame.status === 'error'
                      ? 'border-red-400/40'
                      : frame.status === 'uploading'
                      ? 'border-amber-400/40'
                      : 'border-white/10'
                  }`}
                >
                  {/* Status badge */}
                  <div className='absolute top-2 left-2 z-10 flex flex-col gap-1 items-start'>
                    {frame.status === 'done' && (
                      <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-500/80 text-white'>
                        Done
                      </span>
                    )}
                    {frame.status === 'uploading' && (
                      <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-500/80 text-white'>
                        Uploading…
                      </span>
                    )}
                    {frame.status === 'error' && (
                      <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-500/80 text-white'>
                        Error
                      </span>
                    )}
                    {frame.status === 'pending' &&
                      (existingFrames.has(frame.productKey) ? (
                        <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-sky-500/80 text-white'>
                          Will update existing
                        </span>
                      ) : (
                        <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-white/20 text-white'>
                          New frame
                        </span>
                      ))}
                  </div>

                  {/* Remove button */}
                  {frame.status !== 'uploading' && (
                    <button
                      onClick={() => removeFrame(frame.id)}
                      className='absolute top-2 right-2 z-10 w-5 h-5 rounded-full bg-black/50 text-gray-300 hover:text-white text-xs flex items-center justify-center'
                      title='Remove from queue'
                    >
                      ✕
                    </button>
                  )}

                  {/* Preview */}
                  <div className='h-32 bg-black/40 flex items-center justify-center overflow-hidden p-2 relative'>
                    <img
                      src={frame.previewUrl}
                      alt={frame.name}
                      className='w-full h-full object-contain'
                    />
                  </div>

                  {/* Dimensions badge */}
                  <div className='px-2 pt-1.5 flex items-center justify-between text-[10px] text-gray-400'>
                    <span>{frame.width && frame.height ? `${frame.width}×${frame.height}` : 'Image'}</span>
                    <span>{(frame.file.size / 1024).toFixed(0)} KB</span>
                  </div>

                  {frame.dimensionWarning && (
                    <div className='px-2 text-[10px] text-amber-300'>{frame.dimensionWarning}</div>
                  )}

                  {/* Editable fields */}
                  <div className='p-2 space-y-1.5 flex-1'>
                    <input
                      value={frame.name}
                      onChange={(e) => updateField(frame.id, 'name', e.target.value)}
                      disabled={frame.status === 'uploading' || frame.status === 'done'}
                      className='w-full px-2 py-1 text-xs bg-white/10 border border-white/10 rounded text-white placeholder-gray-500 disabled:opacity-50'
                      placeholder='Frame Name'
                    />
                    <input
                      value={frame.productKey}
                      onChange={(e) => updateField(frame.id, 'productKey', e.target.value)}
                      disabled={frame.status === 'uploading' || frame.status === 'done'}
                      className='w-full px-2 py-1 text-[10px] bg-white/10 border border-white/10 rounded text-gray-300 placeholder-gray-500 disabled:opacity-50 font-mono'
                      placeholder='PRODUCT_KEY'
                    />
                    {frame.error && <p className='text-[10px] text-red-300 leading-tight'>{frame.error}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom Upload Action Bar */}
        {frameFiles.length > 0 && (
          <div className='flex items-center justify-between bg-white/5 border border-white/10 rounded-2xl px-6 py-4'>
            <div className='text-sm text-gray-300'>
              {uploading
                ? `Uploading… ${completedCount} / ${pendingCount + completedCount} done`
                : `${pendingCount} frame${pendingCount !== 1 ? 's' : ''} ready to upload`}
            </div>
            <div className='flex items-center gap-3'>
              {doneCount > 0 && (
                <button
                  type='button'
                  onClick={() => navigate(`/products?category=PROFILE&env=${sharedSettings.Environment}`)}
                  className='px-4 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
                >
                  View in Catalog
                </button>
              )}
              <button
                onClick={handleUploadAll}
                disabled={uploading || pendingCount === 0}
                className='px-6 py-2.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition disabled:opacity-50'
              >
                {uploading
                  ? 'Uploading…'
                  : `Upload ${pendingCount} Frame${pendingCount !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkFrameUpload;
